import { mimeTypeConfigsByType } from '../types/mimeTypes';

export type ContentTypeConfigType = {
  [contentType: string]: {
    selectContentType: {
      label: string;
      icon: string;
      color: string;
    };
    uploadContent: {
      fileUploadLabel: string;
      fileUpload: boolean;
      accept?: string;
      urlUploadLabel: string;
      urlUpload: boolean;
    };
    uploadThumbnail: {
      fromContent: {
        enabled: boolean;
        label: string;
        description: string;
      };
      create: {
        enabled: boolean;
        label: string;
        description: string;
      };
      upload: {
        enabled: boolean;
        label: string;
        description: string;
      };
    };
    addDetails: {
      printable: boolean;
      comments: boolean;
    };
  };
};

export const contentTypeConfig: ContentTypeConfigType = {
  book: {
    selectContentType: {
      label: 'PDF / EPUB',
      icon: 'book',
      color: 'rc-bg-primary rc-text-white',
    },
    uploadContent: {
      fileUploadLabel: 'Upload PDF / EPUB',
      fileUpload: true,
      urlUploadLabel: 'Enter an external Website URL',
      urlUpload: true,
      accept: mimeTypeConfigsByType.book.map((type) => type.mimeType).join(','),
    },
    uploadThumbnail: {
      fromContent: {
        enabled: true,
        label: 'From the Book',
        description: 'Select a page from your book as the thumbnail',
      },
      create: {
        enabled: true,
        label: 'Create',
        description: 'Create a text-based thumbnail',
      },
      upload: {
        enabled: true,
        label: 'Upload',
        description: 'Upload your own image as thumbnail',
      },
    },
    addDetails: {
      printable: true,
      comments: false,
    },
  },
  document: {
    selectContentType: {
      label: 'Word / Excel / PowerPoint',
      icon: 'fileText',
      color: 'rc-bg-orange-700 rc-text-white',
    },
    uploadContent: {
      fileUploadLabel: 'Upload Document',
      fileUpload: true,
      urlUploadLabel: 'Enter an external Website URL',
      urlUpload: false,
      accept: mimeTypeConfigsByType.document
        .map((type) => type.mimeType)
        .join(','),
    },
    uploadThumbnail: {
      fromContent: {
        enabled: false,
        label: 'From Document',
        description: 'Select a page from your document as the thumbnail',
      },
      create: {
        enabled: true,
        label: 'Create',
        description: 'Create a text-based thumbnail',
      },
      upload: {
        enabled: true,
        label: 'Upload',
        description: 'Upload your own image as thumbnail',
      },
    },
    addDetails: {
      printable: true,
      comments: true,
    },
  },
  website: {
    selectContentType: {
      label: 'Website',
      icon: 'globe',
      color: 'rc-bg-red-700 rc-text-white',
    },
    uploadContent: {
      fileUploadLabel: 'Upload File',
      fileUpload: false,
      urlUploadLabel: 'Enter Website URL',
      urlUpload: true,
      accept: mimeTypeConfigsByType.website
        .map((type) => type.mimeType)
        .join(','),
    },
    uploadThumbnail: {
      fromContent: {
        enabled: false,
        label: 'From Website',
        description: 'Use website screenshot as thumbnail',
      },
      create: {
        enabled: true,
        label: 'Create',
        description: 'Create a text-based thumbnail',
      },
      upload: {
        enabled: true,
        label: 'Upload',
        description: 'Upload your own image as thumbnail',
      },
    },
    addDetails: {
      printable: false,
      comments: true,
    },
  },
  audio: {
    selectContentType: {
      label: 'Audio File / Podcast / MP3',
      icon: 'music',
      color: 'rc-bg-amber-500 rc-text-white',
    },
    uploadContent: {
      fileUploadLabel: 'Upload Audio File',
      fileUpload: true,
      urlUploadLabel: 'Enter an external Website URL',
      urlUpload: false,
      accept: mimeTypeConfigsByType.audio
        .map((type) => type.mimeType)
        .join(','),
    },
    uploadThumbnail: {
      fromContent: {
        enabled: false,
        label: 'From Audio',
        description: 'Use audio waveform as thumbnail',
      },
      create: {
        enabled: true,
        label: 'Create',
        description: 'Create a text-based thumbnail',
      },
      upload: {
        enabled: true,
        label: 'Upload',
        description: 'Upload your own image as thumbnail',
      },
    },
    addDetails: {
      printable: false,
      comments: true,
    },
  },
  video: {
    selectContentType: {
      label: 'Video',
      icon: 'video',
      color: 'rc-bg-green-600 rc-text-white',
    },
    uploadContent: {
      fileUploadLabel: 'Upload Video File',
      fileUpload: true,
      urlUploadLabel: 'Enter an external Website URL',
      urlUpload: false,
      accept: mimeTypeConfigsByType.video
        .map((type) => type.mimeType)
        .join(','),
    },
    uploadThumbnail: {
      fromContent: {
        enabled: false,
        label: 'From Video',
        description: 'Use video frame as thumbnail',
      },
      create: {
        enabled: true,
        label: 'Create',
        description: 'Create a text-based thumbnail',
      },
      upload: {
        enabled: true,
        label: 'Upload',
        description: 'Upload your own image as thumbnail',
      },
    },
    addDetails: {
      printable: false,
      comments: true,
    },
  },
  image: {
    selectContentType: {
      label: 'Image',
      icon: 'image',
      color: 'rc-bg-purple-700 rc-text-white',
    },
    uploadContent: {
      fileUploadLabel: 'Upload Image File',
      fileUpload: true,
      urlUploadLabel: 'Enter an external Website URL',
      urlUpload: false,
      accept: mimeTypeConfigsByType.image
        .map((type) => type.mimeType)
        .join(','),
    },
    uploadThumbnail: {
      fromContent: {
        enabled: true,
        label: 'Use Image',
        description: 'Use the uploaded image as thumbnail',
      },
      create: {
        enabled: true,
        label: 'Create',
        description: 'Create a text-based thumbnail',
      },
      upload: {
        enabled: false,
        label: 'Upload',
        description: 'Upload your own image as thumbnail',
      },
    },
    addDetails: {
      printable: false,
      comments: true,
    },
  },
} as const;
